import { baseAPI } from '@/api/axios';
import { generateUrl } from '../utils/helpers';

const getOptions = (
  tenantId,
  groupId = null,
  contentType = 'application/json'
) => {
  const headers = { 'Content-Type': contentType };
  if (tenantId) headers['TenantId'] = tenantId;
  if (groupId) headers['Group-Id'] = groupId;
  return { headers };
};

export default {
  exportUsageData({ orgId, groupId, payload }) {
    const url = generateUrl('organization/exportUsageData', payload);
    return baseAPI.get(url, getOptions(orgId, groupId));
  },
  updateUserDetail({ orgId, payload }) {
    return baseAPI.post(`user/${payload.id}`, payload, getOptions(orgId));
  },
  getUsers({ orgId, userId }) {
    let query = userId ? `?userId=${userId}` : '';
    return baseAPI.get(`organization/${orgId}/getUsers${query}`);
  },
  deleteCourseAssignment({ orgId, groupId, courseAssignmentId }) {
    return baseAPI.delete(
      `courseAssignment/${courseAssignmentId}/delete`,
      getOptions(orgId, groupId)
    );
  },
  bulkEditCourseAssignments({ payload, orgId }) {
    return baseAPI.post(
      'courseAssignment/bulkEdit',
      payload,
      getOptions(orgId)
    );
  },
  deleteSupportRequest({ orgId, groupId, requestId }) {
    const payload = {};
    return baseAPI.post(
      `/supportRequests/${requestId}/resolve`,
      payload,
      getOptions(orgId, groupId)
    );
  },

  // baseAPIs
  getOrganizationMetaData({ orgId, groupId }) {
    return baseAPI.get(`organization/metadata`, getOptions(orgId, groupId));
  },
  getExternalAttributes({ orgId, groupId, name, value }) {
    return baseAPI.get(`user/externalAttributes/list`, {
      ...getOptions(orgId, groupId),
      params: { name, value },
    });
  },
  getAllSupportRequests({ orgId, groupId, userId }) {
    const url = generateUrl('organization/allSupportRequests', { userId });
    return baseAPI.get(url, getOptions(orgId, groupId));
  },
  getSupportRequests({ orgId, userId }) {
    return baseAPI.get(
      `player/supportRequests?userId=${userId}`,
      getOptions(orgId)
    );
  },
  getSyncStatusForOrganization({ orgId }) {
    return baseAPI.get(`organization/syncStatus`, getOptions(orgId));
  },
  syncUsersFromOrganization({ orgId, groupId }) {
    const payload = {};
    return baseAPI.post(
      'organization/externalIdpSync',
      payload,
      getOptions(orgId, groupId)
    );
  },
  updateAssignedCourse({ orgId, payload }) {
    return baseAPI.post(
      `courseAssignment/${payload.id}`,
      payload,
      getOptions(orgId)
    );
  },
  getInPersonTrainingInstructors({ orgId, groupId = null, search = '' }) {
    return baseAPI.get('courseAssignment/inPersonTraining/instructors', {
      ...getOptions(orgId, groupId),
      params: { search: search.trim() },
    });
  },
  submitInPersonTraining({
    orgId,
    groupId = null,
    courseAssignmentId,
    payload,
  }) {
    return baseAPI.post(
      `courseAssignment/${courseAssignmentId}/inPersonTraining`,
      payload,
      getOptions(orgId, groupId)
    );
  },
  getUserDetail({
    orgId,
    userId,
    dateRangeStart,
    dateRangeEnd,
    cycleId,
    cycleIds,
  }) {
    const url = generateUrl('organization/getClientUserDetail', {
      userId,
      startDate: dateRangeStart,
      endDate: dateRangeEnd,
      cycleId,
      cycleIds,
    });
    return baseAPI.get(url, getOptions(orgId));
  },
  getUsersUsageData({ orgId, groupId, payload }) {
    return baseAPI.post(
      `organization/users/usageData`,
      payload,
      getOptions(orgId, groupId)
    );
  },
  getDailyUsageData({ orgId, groupId, payload }) {
    return baseAPI.post(
      `organization/dailyUsage`,
      payload,
      getOptions(orgId, groupId)
    );
  },
  getOfficersInOrgDashboard({ orgId, groupId, payload }) {
    const url = generateUrl('organization/users/search', payload);
    return baseAPI.get(url, getOptions(orgId, groupId));
  },
  getAssignedUsersWithEligibleGroups({ orgId, groupId, payload }) {
    return baseAPI.post(
      'courseAssignment/assignedUsersWithEligibleGroups',
      payload,
      getOptions(orgId, groupId)
    );
  },
  assignCoursesBatch({ orgId, groupId, payload }) {
    return baseAPI.post(
      `courseAssignment/create-batch`,
      payload,
      getOptions(orgId, groupId)
    );
  },
  getTrainingManagers({ orgId, groupId, params = {} }) {
    return baseAPI.get('organization/trainingManagers', {
      ...getOptions(orgId, groupId),
      params,
    });
  },
  validateUseOfForcePolicyUrl({ orgId, groupId, payload }) {
    return baseAPI.post(
      'organization/validateUseOfForcePolicyUrl',
      payload,
      getOptions(orgId, groupId)
    );
  },
  getOrgPolicyByCourseId({ orgId, courseId }) {
    return baseAPI.get(`courses/${courseId}/orgPolicy`, getOptions(orgId));
  },
  saveOrgPolicy({ orgId, courseId, payload }) {
    return baseAPI.post(
      `courses/${courseId}/orgPolicy`,
      payload,
      getOptions(orgId)
    );
  },
  savePolicyDocumentLinks({ orgId, courseUnifierId, payload }) {
    return baseAPI.post(
      `/courses/${courseUnifierId}/policyDocumentLinks`,
      payload,
      getOptions(orgId)
    );
  },
  requestCompletionStatusReport({ orgId, groupId, payload }) {
    return baseAPI.post(
      'courseAssignment/downloads/completionStatusReport',
      payload,
      getOptions(orgId, groupId)
    );
  },
  getBulkEditReportUrl(orgId) {
    return baseAPI.get(
      'CourseAssignment/BulkEdit/ReportUrl',
      getOptions(orgId)
    );
  },
  getCourseProgress({ sortBy, sortDirection, orgId }) {
    return baseAPI.get(
      `organization/licensedCourseProgress${sortBy ? `&sortBy=${sortBy}` : ''}${
        sortDirection ? `&sortDirection=${sortDirection}` : ''
      }`,
      getOptions(orgId)
    );
  },
  getUserImportCSVTemplate(orgId) {
    return baseAPI.get(
      'organization/downloads/csvUserImportTemplate',
      getOptions(orgId)
    );
  },
  getCourseCycles(orgId, onlyCyclesWithAssignments) {
    return baseAPI.get(`organization/${orgId}/cycles`, {
      ...getOptions(orgId),
      params: { onlyCyclesWithAssignments },
    });
  },
  getAccreditationCycleGroups(orgId, source) {
    return baseAPI.get(`organization/${orgId}/accreditation-cycle-groups`, {
      ...getOptions(orgId),
      params: { source },
    });
  },
};
